import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button, Input, Label, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { useAuthStore } from '@/stores';
import toast from 'react-hot-toast';
import { Shield, Users, FileText, Trash2, UserPlus, X, Download, HardDrive, Loader2, Webhook, Upload, FileDown, BarChart3, Eye, TrendingUp } from 'lucide-react';
import type { ApiResponse, PaginatedData, User, AuditLog, Backup } from '@/types';
import { useAnalyticsStats, useTopPages, useDailyViewStats } from '@/hooks';

export function Admin() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'trash' | 'backups' | 'webhooks' | 'import' | 'analytics'>('users');
  const [showCreateUser, setShowCreateUser] = useState(false);

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">访问受限</h1>
        <p className="text-muted-foreground">您没有权限访问此页面</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-12 space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Shield className="h-6 w-6" />
        管理面板
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('users')}
        >
          <Users className="h-4 w-4 inline mr-2" />
          用户管理
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('audit')}
        >
          <FileText className="h-4 w-4 inline mr-2" />
          审计日志
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'trash'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('trash')}
        >
          <Trash2 className="h-4 w-4 inline mr-2" />
          回收站
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'backups'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('backups')}
        >
          <HardDrive className="h-4 w-4 inline mr-2" />
          备份管理
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'webhooks'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('webhooks')}
        >
          <Webhook className="h-4 w-4 inline mr-2" />
          Webhook
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'import'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('import')}
        >
          <Upload className="h-4 w-4 inline mr-2" />
          导入/导出
        </button>
        <button
          className={`px-4 py-2 border-b-2 transition-colors ${
            activeTab === 'analytics'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('analytics')}
        >
          <BarChart3 className="h-4 w-4 inline mr-2" />
          访问统计
        </button>
      </div>

      {/* Content */}
      {activeTab === 'users' && (
        <UserManagement
          showCreateUser={showCreateUser}
          setShowCreateUser={setShowCreateUser}
        />
      )}
      {activeTab === 'audit' && <AuditLogs />}
      {activeTab === 'trash' && <TrashManagement />}
      {activeTab === 'backups' && <BackupManagement />}
      {activeTab === 'webhooks' && <WebhookManagement />}
      {activeTab === 'import' && <ImportExport />}
      {activeTab === 'analytics' && <AnalyticsPanel />}
    </div>
  );
}

function UserManagement({
  showCreateUser,
  setShowCreateUser,
}: {
  showCreateUser: boolean;
  setShowCreateUser: (show: boolean) => void;
}) {
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['adminUsers', page],
    queryFn: async (): Promise<PaginatedData<User>> => {
      const response = await api.get<ApiResponse<PaginatedData<User>>>(`/admin/users?page=${page}&perPage=10`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch users');
      }
      return response.data.data;
    },
  });

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      toast.success('角色已更新');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '更新失败');
    }
  };

  const handleDisableUser = async (userId: number) => {
    if (!confirm('确定要禁用此用户吗？')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('用户已禁用');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '操作失败');
    }
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-muted rounded" />;
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateUser(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          添加用户
        </Button>
      </div>

      {showCreateUser && (
        <CreateUserForm
          onClose={() => setShowCreateUser(false)}
          onSuccess={() => {
            setShowCreateUser(false);
            refetch();
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>用户列表</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">ID</th>
                  <th className="text-left py-2 px-4">用户名</th>
                  <th className="text-left py-2 px-4">邮箱</th>
                  <th className="text-left py-2 px-4">角色</th>
                  <th className="text-left py-2 px-4">状态</th>
                  <th className="text-left py-2 px-4">操作</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((user) => (
                  <tr key={user.id} className="border-b">
                    <td className="py-2 px-4">{user.id}</td>
                    <td className="py-2 px-4">{user.username}</td>
                    <td className="py-2 px-4">{user.email}</td>
                    <td className="py-2 px-4">
                      <select
                        className={`px-2 py-1 rounded text-sm ${getRoleColor(user.role)}`}
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={user.id === data.items[0]?.id}
                      >
                        <option value="reader">读者</option>
                        <option value="editor">编辑者</option>
                        <option value="admin">管理员</option>
                      </select>
                    </td>
                    <td className="py-2 px-4">
                      <span className={user.is_active ? 'text-green-600' : 'text-red-600'}>
                        {user.is_active ? '正常' : '已禁用'}
                      </span>
                    </td>
                    <td className="py-2 px-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const userId = user.id;
                            const newPassword = prompt('请输入新密码（至少8位，包含大小写字母和数字）:');
                            if (newPassword) {
                              if (newPassword.length < 8) {
                                toast.error('密码至少需要 8 个字符');
                                return;
                              }
                              if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
                                toast.error('密码必须包含大小写字母和数字');
                                return;
                              }
                              api.post(`/admin/users/${userId}/reset-password`, { password: newPassword })
                                .then(() => toast.success('密码已重置'))
                                .catch((err) => toast.error(err.response?.data?.error?.message || '重置失败'));
                            }
                          }}
                        >
                          重置密码
                        </Button>
                        {user.is_active ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDisableUser(user.id)}
                          >
                            禁用
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                上一页
              </Button>
              <span className="py-2 px-4 text-sm text-muted-foreground">
                第 {page} / {data.totalPages} 页
              </span>
              <Button
                variant="outline"
                disabled={page === data.totalPages}
                onClick={() => setPage(page + 1)}
              >
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CreateUserForm({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'reader' | 'editor' | 'admin'>('reader');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !username || !password) {
      toast.error('请填写所有字段');
      return;
    }

    if (password.length < 8) {
      toast.error('密码至少需要 8 个字符');
      return;
    }

    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
      toast.error('密码必须包含大小写字母和数字');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post<ApiResponse<User>>('/admin/users', {
        email,
        username,
        password,
        role,
      });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || '创建失败');
      }
      toast.success('用户创建成功');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || error.message || '创建失败');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>添加新用户</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">邮箱</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">用户名</Label>
              <Input
                id="username"
                type="text"
                placeholder="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                minLength={3}
                maxLength={50}
                pattern="[a-zA-Z0-9_\-]+"
                title="用户名只能包含字母、数字、下划线和连字符"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">密码</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <p className="text-xs text-muted-foreground">
                至少 8 个字符，包含大小写字母和数字
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">角色</Label>
              <select
                id="role"
                className="w-full px-3 py-2 border rounded-md bg-background"
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
              >
                <option value="reader">读者</option>
                <option value="editor">编辑者</option>
                <option value="admin">管理员</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? '创建中...' : '创建用户'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              取消
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AuditLogs() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['auditLogs', page],
    queryFn: async (): Promise<PaginatedData<AuditLog>> => {
      const response = await api.get<ApiResponse<PaginatedData<AuditLog>>>(`/admin/audit-logs?page=${page}&perPage=20`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch audit logs');
      }
      return response.data.data;
    },
  });

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      register: '注册',
      login: '登录',
      logout: '登出',
      create_page: '创建页面',
      edit_page: '编辑页面',
      delete_page: '删除页面',
      restore_page: '恢复页面',
      change_role: '更改角色',
      change_visibility: '更改可见性',
      upload_attachment: '上传附件',
      delete_attachment: '删除附件',
      create_user: '创建用户',
      disable_user: '禁用用户',
      password_reset: '修改密码',
    };
    return labels[action] || action;
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-muted rounded" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>审计日志</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4">时间</th>
                <th className="text-left py-2 px-4">用户</th>
                <th className="text-left py-2 px-4">操作</th>
                <th className="text-left py-2 px-4">对象</th>
                <th className="text-left py-2 px-4">IP</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((log) => (
                <tr key={log.id} className="border-b">
                  <td className="py-2 px-4 text-sm text-muted-foreground">
                    {new Date(log.created_at).toLocaleString('zh-CN')}
                  </td>
                  <td className="py-2 px-4">{log.user?.username || '-'}</td>
                  <td className="py-2 px-4">{getActionLabel(log.action)}</td>
                  <td className="py-2 px-4 text-sm text-muted-foreground">
                    {log.target_type ? `${log.target_type}#${log.target_id}` : '-'}
                  </td>
                  <td className="py-2 px-4 text-sm text-muted-foreground">
                    {log.ip_address || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data && data.totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              上一页
            </Button>
            <span className="py-2 px-4 text-sm text-muted-foreground">
              第 {page} / {data.totalPages} 页
            </span>
            <Button
              variant="outline"
              disabled={page === data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TrashManagement() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>回收站</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground text-center py-8">
          暂无已删除的页面
        </p>
      </CardContent>
    </Card>
  );
}

function BackupManagement() {
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['backups'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ items: Backup[] }>>('/admin/backups');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch backups');
      }
      return response.data.data;
    },
  });

  const handleCreateBackup = async () => {
    if (!confirm('确定要创建备份吗？这可能需要一些时间。')) return;

    setIsCreating(true);
    try {
      const response = await api.post<ApiResponse>('/admin/backup');
      if (!response.data.success) {
        throw new Error(response.data.error?.message || '创建备份失败');
      }
      toast.success('备份创建成功');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '创建备份失败');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (key: string) => {
    try {
      const response = await api.get(`/admin/backups/${encodeURIComponent(key)}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = key.split('/').pop() || 'backup.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('下载成功');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '下载失败');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreateBackup} disabled={isCreating}>
          {isCreating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <HardDrive className="h-4 w-4 mr-2" />
          )}
          {isCreating ? '创建中...' : '创建备份'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>备份列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="animate-pulse h-32 bg-muted rounded" />
          ) : !data?.items?.length ? (
            <p className="text-muted-foreground text-center py-8">
              暂无备份
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">文件名</th>
                    <th className="text-left py-2 px-4">创建时间</th>
                    <th className="text-left py-2 px-4">大小</th>
                    <th className="text-left py-2 px-4">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((backup) => (
                    <tr key={backup.key} className="border-b">
                      <td className="py-2 px-4 font-mono text-sm">
                        {backup.key.split('/').pop()}
                      </td>
                      <td className="py-2 px-4 text-sm text-muted-foreground">
                        {new Date(backup.created_at).toLocaleString('zh-CN')}
                      </td>
                      <td className="py-2 px-4 text-sm">
                        {formatBytes(backup.size)}
                      </td>
                      <td className="py-2 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(backup.key)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          下载
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface Webhook {
  id: number;
  name: string;
  url: string;
  secret: string | null;
  events: string[];
  is_active: boolean;
  created_at: string;
  last_triggered_at: string | null;
}

function WebhookManagement() {
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<string[]>([]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Webhook[]>>('/webhooks');
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch webhooks');
      }
      return response.data.data;
    },
  });

  const createWebhook = useMutation({
    mutationFn: async () => {
      const response = await api.post<ApiResponse<Webhook>>('/webhooks', {
        name,
        url,
        secret: secret || undefined,
        events,
      });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to create webhook');
      }
      return response.data.data;
    },
    onSuccess: () => {
      toast.success('Webhook 创建成功');
      setShowCreate(false);
      setName('');
      setUrl('');
      setSecret('');
      setEvents([]);
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || '创建失败');
    },
  });

  const deleteWebhook = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete<ApiResponse>(`/webhooks/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to delete webhook');
      }
      return response.data.data;
    },
    onSuccess: () => {
      toast.success('Webhook 已删除');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || '删除失败');
    },
  });

  const toggleWebhook = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const response = await api.patch<ApiResponse<Webhook>>(`/webhooks/${id}`, {
        is_active: isActive,
      });
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to update webhook');
      }
      return response.data.data;
    },
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || '更新失败');
    },
  });

  const eventTypes = [
    { value: 'page.created', label: '页面创建' },
    { value: 'page.updated', label: '页面更新' },
    { value: 'page.deleted', label: '页面删除' },
    { value: 'comment.created', label: '评论创建' },
    { value: 'comment.deleted', label: '评论删除' },
  ];

  const toggleEvent = (event: string) => {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? '取消' : '创建 Webhook'}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>创建 Webhook</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (events.length === 0) {
                  toast.error('请选择至少一个事件类型');
                  return;
                }
                createWebhook.mutate();
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="webhook-name">名称</Label>
                  <Input
                    id="webhook-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Webhook 名称"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">URL</Label>
                  <Input
                    id="webhook-url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/webhook"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="webhook-secret">密钥（可选）</Label>
                <Input
                  id="webhook-secret"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="用于签名验证"
                />
              </div>
              <div className="space-y-2">
                <Label>事件类型</Label>
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map((type) => (
                    <label
                      key={type.value}
                      className={`px-3 py-1 rounded cursor-pointer transition-colors ${
                        events.includes(type.value)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={events.includes(type.value)}
                        onChange={() => toggleEvent(type.value)}
                      />
                      {type.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createWebhook.isPending}>
                  {createWebhook.isPending ? '创建中...' : '创建'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreate(false)}
                >
                  取消
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Webhook 列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="animate-pulse h-32 bg-muted rounded" />
          ) : !data?.length ? (
            <p className="text-muted-foreground text-center py-8">
              暂无 Webhook
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">名称</th>
                    <th className="text-left py-2 px-4">URL</th>
                    <th className="text-left py-2 px-4">事件</th>
                    <th className="text-left py-2 px-4">状态</th>
                    <th className="text-left py-2 px-4">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((webhook) => (
                    <tr key={webhook.id} className="border-b">
                      <td className="py-2 px-4 font-medium">{webhook.name}</td>
                      <td className="py-2 px-4 text-sm text-muted-foreground max-w-xs truncate">
                        {webhook.url}
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex flex-wrap gap-1">
                          {webhook.events.map((event) => (
                            <span
                              key={event}
                              className="px-2 py-0.5 bg-muted rounded text-xs"
                            >
                              {eventTypes.find((t) => t.value === event)?.label || event}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-4">
                        <button
                          className={`px-2 py-1 rounded text-xs ${
                            webhook.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          onClick={() =>
                            toggleWebhook.mutate({
                              id: webhook.id,
                              isActive: !webhook.is_active,
                            })
                          }
                        >
                          {webhook.is_active ? '启用' : '禁用'}
                        </button>
                      </td>
                      <td className="py-2 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            if (confirm('确定要删除此 Webhook 吗？')) {
                              deleteWebhook.mutate(webhook.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ImportExport() {
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const handleExportAll = async () => {
    try {
      const response = await api.get('/admin/export', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wiki-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('导出成功');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '导出失败');
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportResults(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate the structure
      if (!data.pages || !Array.isArray(data.pages)) {
        throw new Error('无效的导入文件格式');
      }

      const results = {
        success: 0,
        failed: 0,
        errors: [] as string[],
      };

      for (const page of data.pages) {
        try {
          await api.post('/pages', {
            title: page.title,
            content: page.content,
            visibility: page.visibility || 'public',
            tags: page.tags || [],
          });
          results.success++;
        } catch (err: any) {
          results.failed++;
          results.errors.push(`${page.title}: ${err.response?.data?.error?.message || err.message}`);
        }
      }

      setImportResults(results);

      if (results.success > 0) {
        toast.success(`成功导入 ${results.success} 个页面`);
      }
      if (results.failed > 0) {
        toast.error(`${results.failed} 个页面导入失败`);
      }
    } catch (error: any) {
      toast.error(error.message || '导入失败');
    } finally {
      setIsImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleImportMarkdown = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsImporting(true);
    setImportResults(null);

    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[],
    };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const content = await file.text();
        const title = file.name.replace(/\.md$/i, '');

        await api.post('/pages', {
          title,
          content,
          visibility: 'public',
        });
        results.success++;
      } catch (err: any) {
        results.failed++;
        results.errors.push(`${file.name}: ${err.response?.data?.error?.message || err.message}`);
      }
    }

    setImportResults(results);

    if (results.success > 0) {
      toast.success(`成功导入 ${results.success} 个页面`);
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} 个文件导入失败`);
    }

    setIsImporting(false);
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5" />
            导出页面
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            导出所有页面为 JSON 格式，包含标题、内容、标签等信息。
          </p>
          <Button onClick={handleExportAll}>
            <Download className="h-4 w-4 mr-2" />
            导出所有页面
          </Button>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            导入页面
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Import JSON */}
          <div className="space-y-3">
            <h4 className="font-medium">导入 JSON 文件</h4>
            <p className="text-sm text-muted-foreground">
              导入之前导出的 JSON 格式备份文件。
            </p>
            <div>
              <input
                type="file"
                accept=".json"
                onChange={handleFileImport}
                disabled={isImporting}
                className="hidden"
                id="import-json"
              />
              <Button
                variant="outline"
                disabled={isImporting}
                onClick={() => document.getElementById('import-json')?.click()}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    导入中...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    选择 JSON 文件
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="border-t pt-6" />

          {/* Import Markdown */}
          <div className="space-y-3">
            <h4 className="font-medium">导入 Markdown 文件</h4>
            <p className="text-sm text-muted-foreground">
              批量导入 Markdown 文件，每个文件将创建为一个新页面。文件名（不含扩展名）将作为页面标题。
            </p>
            <div>
              <input
                type="file"
                accept=".md,.markdown"
                multiple
                onChange={handleImportMarkdown}
                disabled={isImporting}
                className="hidden"
                id="import-md"
              />
              <Button
                variant="outline"
                disabled={isImporting}
                onClick={() => document.getElementById('import-md')?.click()}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    导入中...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    选择 Markdown 文件（可多选）
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className={`p-4 rounded-lg ${
              importResults.failed > 0 ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-green-50 dark:bg-green-900/20'
            }`}>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600 dark:text-green-400">
                  成功: {importResults.success}
                </span>
                {importResults.failed > 0 && (
                  <span className="text-red-600 dark:text-red-400">
                    失败: {importResults.failed}
                  </span>
                )}
              </div>
              {importResults.errors.length > 0 && (
                <div className="mt-2 text-sm text-muted-foreground">
                  <p className="font-medium">错误详情:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    {importResults.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {importResults.errors.length > 5 && (
                      <li>... 还有 {importResults.errors.length - 5} 个错误</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const { data: stats, isLoading: statsLoading } = useAnalyticsStats({ days });
  const { data: topPages, isLoading: pagesLoading } = useTopPages({ days, limit: 10 });
  const { data: dailyStats, isLoading: dailyLoading } = useDailyViewStats({ days });

  const isLoading = statsLoading || pagesLoading || dailyLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse h-32 bg-muted rounded" />
        <div className="animate-pulse h-64 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">访问统计</h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-3 py-2 border rounded-md bg-background"
        >
          <option value={7}>最近 7 天</option>
          <option value={30}>最近 30 天</option>
          <option value={90}>最近 90 天</option>
        </select>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">总访问量</p>
                <p className="text-2xl font-bold">{stats?.totalViews.toLocaleString() || 0}</p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">独立访客</p>
                <p className="text-2xl font-bold">{stats?.uniqueVisitors.toLocaleString() || 0}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">平均访问/页</p>
                <p className="text-2xl font-bold">{stats?.avgViewsPerPage || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Pages */}
      <Card>
        <CardHeader>
          <CardTitle>热门页面</CardTitle>
        </CardHeader>
        <CardContent>
          {topPages && topPages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">#</th>
                    <th className="text-left py-2 px-4">页面</th>
                    <th className="text-right py-2 px-4">访问量</th>
                    <th className="text-right py-2 px-4">独立访客</th>
                  </tr>
                </thead>
                <tbody>
                  {topPages.map((page, index) => (
                    <tr key={page.page_id} className="border-b">
                      <td className="py-2 px-4 text-muted-foreground">{index + 1}</td>
                      <td className="py-2 px-4">
                        <a
                          href={`/pages/${page.page_slug}`}
                          className="text-primary hover:underline"
                        >
                          {page.page_title}
                        </a>
                      </td>
                      <td className="py-2 px-4 text-right">{page.view_count.toLocaleString()}</td>
                      <td className="py-2 px-4 text-right">{page.unique_visitors.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">暂无访问数据</p>
          )}
        </CardContent>
      </Card>

      {/* Daily Stats Chart */}
      <Card>
        <CardHeader>
          <CardTitle>每日访问趋势</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyStats && dailyStats.length > 0 ? (
            <div className="space-y-4">
              {/* Simple bar chart representation */}
              <div className="space-y-2">
                {dailyStats.slice(0, 14).reverse().map((day) => {
                  const maxViews = Math.max(...dailyStats.map(d => d.view_count));
                  const percentage = maxViews > 0 ? (day.view_count / maxViews) * 100 : 0;
                  return (
                    <div key={day.date} className="flex items-center gap-4">
                      <div className="w-24 text-sm text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="w-20 text-sm text-right">
                        <span className="font-medium">{day.view_count}</span>
                        <span className="text-muted-foreground ml-1">
                          ({day.unique_visitors})
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-sm text-muted-foreground">
                显示最近 14 天数据（括号内为独立访客数）
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">暂无访问数据</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
